import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api, type ApiKeyRecord, type WebhookDelivery, type WebhookEndpoint } from '../lib/api.js';
import { SecretReveal } from '../components/SecretReveal.js';
import { formatDate } from '../lib/format.js';
import { currentBrand } from '../lib/brand.js';

const SCOPES = [
  { id: 'read:meetings', label: 'Read meetings' },
  { id: 'write:meetings', label: 'Write meetings' },
  { id: 'read:actions', label: 'Read decisions & actions' },
  { id: 'write:actions', label: 'Edit decisions & actions' },
  { id: 'webhooks', label: 'Manage webhooks' },
  { id: 'push', label: 'Push actions' },
];

const EVENTS = [
  { id: 'meeting.completed', label: 'meeting.completed' },
  { id: 'meeting.failed', label: 'meeting.failed' },
  { id: 'action.created', label: 'action.created' },
  { id: 'action.updated', label: 'action.updated' },
  { id: 'action.pushed', label: 'action.pushed' },
];

export function DevelopersPage(): JSX.Element {
  const brand = currentBrand();
  const { hash } = useLocation();
  useEffect(() => {
    const id = hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <div>
      <h1 className="page-title">{brand.id === 'agentmeetings' ? 'Developer tools' : 'Developers'}</h1>
      <p className="page-subtitle">
        API keys for agents (<code>tnk_…</code>), MCP, and outbound webhooks. The shared script token still works.
      </p>
      <div className="account-grid">
        <ApiKeysPanel />
        <McpPanel />
        <WebhooksPanel />
      </div>
    </div>
  );
}

function ApiKeysPanel(): JSX.Element {
  const [keys, setKeys] = useState<ApiKeyRecord[] | null>(null);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['read:meetings', 'read:actions']);
  const [fresh, setFresh] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh(): Promise<void> {
    setKeys(await api.listApiKeys());
  }

  useEffect(() => {
    void refresh().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  function toggle(scope: string): void {
    setScopes((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));
  }

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy || !name.trim() || scopes.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createApiKey(name.trim(), scopes);
      setFresh(created.token ?? null);
      setName('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string): Promise<void> {
    setError(null);
    try {
      await api.revokeApiKey(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <section className="panel" id="api-keys">
      <div className="panel__title">API keys</div>
      <p className="form-help" style={{ marginTop: 0 }}>
        Send <code>Authorization: Bearer tnk_…</code> to <code>/api/v1</code> and to the MCP endpoint. No browser cookie.
        The raw key is shown once.
      </p>
      <form onSubmit={(e) => void onCreate(e)}>
        <div className="form-row">
          <label className="form-label" htmlFor="keyName">Name</label>
          <input id="keyName" value={name} onChange={(e) => setName(e.target.value)} placeholder="Strategy agent" required />
        </div>
        <div className="scope-list">
          {SCOPES.map((s) => (
            <label key={s.id}>
              <input type="checkbox" checked={scopes.includes(s.id)} onChange={() => toggle(s.id)} />
              {s.label}
            </label>
          ))}
        </div>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn btn--sm" disabled={busy || scopes.length === 0} style={{ marginTop: 12 }}>
          {busy ? 'Creating…' : 'Create key'}
        </button>
      </form>
      {fresh && (
        <div style={{ marginTop: 12 }}>
          <SecretReveal label="API key" value={fresh} onDismiss={() => setFresh(null)} />
        </div>
      )}
      <ul className="ledger-list" style={{ marginTop: 16 }}>
        {(keys ?? []).map((key) => (
          <li key={key.id} className="ledger-row">
            <div className="ledger-row__top">
              <strong>{key.name}</strong>
              <code>{key.prefix}…</code>
              <button type="button" className="btn btn--danger-ghost btn--sm" onClick={() => void revoke(key.id)}>
                Revoke
              </button>
            </div>
            <div className="panel__subtle">
              {key.scopes.join(', ')}
              {key.lastUsedAt ? ` · used ${formatDate(key.lastUsedAt)}` : ' · never used'}
            </div>
          </li>
        ))}
        {keys && keys.length === 0 && <p className="panel__subtle">No keys yet.</p>}
      </ul>
    </section>
  );
}

function McpPanel(): JSX.Element {
  const brand = currentBrand();
  const endpoint = `${window.location.origin}/mcp`;
  const example = JSON.stringify({
    mcpServers: {
      [brand.mcpServerKey]: {
        url: endpoint,
        headers: { Authorization: 'Bearer tnk_YOUR_KEY' },
      },
    },
  }, null, 2);
  return (
    <section className="panel" id="agents">
      <span id="mcp" />
      <div className="panel__title">MCP</div>
      <p className="form-help" style={{ marginTop: 0 }}>
        Claude Desktop, Cursor, and other MCP hosts call <code>{endpoint}</code> with
        <code> Authorization: Bearer tnk_…</code>. Create a key with <code>read:meetings</code> and
        <code> read:actions</code>. Add <code>push</code> only if that agent should push actions.
        Search and meeting reads do not return the transcript; ask for a short excerpt with start and end times.
      </p>
      <pre className="mcp-config">{example}</pre>
      <p className="form-help">
        Hosts that only launch a local command can use <code>npx -y mcp-remote {endpoint} --header "Authorization: Bearer tnk_YOUR_KEY"</code>.
        {brand.id === 'takenotes'
          ? <> On this server, <code>TAKENOTES_API_KEY=tnk_… npm run mcp -w api</code> speaks stdio against the local database.</>
          : <> On the machine that holds the database, <code>npm run mcp -w api</code> speaks stdio. Put your API key in the environment first.</>}
      </p>
    </section>
  );
}

function WebhooksPanel(): JSX.Element {
  const [hooks, setHooks] = useState<WebhookEndpoint[] | null>(null);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['meeting.completed', 'meeting.failed']);
  const [fresh, setFresh] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);

  async function refresh(): Promise<void> {
    setHooks(await api.listWebhooks());
  }

  useEffect(() => {
    void refresh().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  function toggle(event: string): void {
    setEvents((cur) => (cur.includes(event) ? cur.filter((e) => e !== event) : [...cur, event]));
  }

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy || !url.trim() || events.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createWebhook(url.trim(), events);
      setFresh(created.secret ?? null);
      setUrl('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setError(null);
    try {
      await api.deleteWebhook(id);
      if (openId === id) setOpenId(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function showDeliveries(id: string): Promise<void> {
    setOpenId(id);
    setDeliveries(await api.listWebhookDeliveries(id));
  }

  const brand = currentBrand();
  return (
    <section className="panel" id="webhooks">
      <div className="panel__title">Outbound webhooks</div>
      <p className="form-help" style={{ marginTop: 0 }}>
        {brand.name} POSTs the events you select with
        <code> {brand.signatureHeader}: sha256=…</code> (HMAC of the raw body). The signing secret is shown once.
        Failed deliveries retry and stay in the log.
      </p>
      <form onSubmit={(e) => void onCreate(e)}>
        <div className="form-row">
          <label className="form-label" htmlFor="hookUrl">Endpoint URL</label>
          <input id="hookUrl" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`https://example.com/hooks/${currentBrand().hookExampleHost}`} required />
        </div>
        <div className="scope-list">
          {EVENTS.map((event) => (
            <label key={event.id}>
              <input type="checkbox" checked={events.includes(event.id)} onChange={() => toggle(event.id)} />
              {event.label}
            </label>
          ))}
        </div>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn btn--sm" disabled={busy || events.length === 0} style={{ marginTop: 12 }}>
          {busy ? 'Saving…' : 'Register endpoint'}
        </button>
      </form>
      {fresh && (
        <div style={{ marginTop: 12 }}>
          <SecretReveal label="Signing secret" value={fresh} onDismiss={() => setFresh(null)} />
        </div>
      )}
      <ul className="ledger-list" id="logs" style={{ marginTop: 16 }}>
        {(hooks ?? []).map((hook) => (
          <li key={hook.id} className="ledger-row">
            <div className="ledger-row__top">
              <span className="ledger-url">{hook.url}</span>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void showDeliveries(hook.id)}>
                Deliveries
              </button>
              <button type="button" className="btn btn--danger-ghost btn--sm" onClick={() => void remove(hook.id)}>
                Delete
              </button>
            </div>
            <div className="panel__subtle">
              {hook.events.join(', ')} · secret {hook.secretPrefix}…
            </div>
            {openId === hook.id && (
              <ul className="delivery-log">
                {deliveries.length === 0 && <li>No deliveries yet.</li>}
                {deliveries.map((d) => (
                  <li key={d.id}>
                    <strong>{d.status}</strong> · {d.eventType} · {d.attempts} attempt{d.attempts === 1 ? '' : 's'}
                    {d.responseStatus ? ` · HTTP ${d.responseStatus}` : ''}
                    {d.lastError ? ` · ${d.lastError}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
        {hooks && hooks.length === 0 && <p className="panel__subtle">No endpoints yet.</p>}
      </ul>
    </section>
  );
}
